import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { MagnifyingGlass, Plus, Scales } from "@phosphor-icons/react";

const ROLE_LABEL = {
  lead_investigator: "Lead Investigator",
  investigator: "Investigator",
  investigator_reviewer: "Investigator Reviewer",
  legal_investigation_rep: "Legal Investigation Rep",
};
const STATUS_STYLE = {
  open: "bg-blue-100 text-blue-800",
  in_review: "bg-amber-100 text-amber-800",
  closed: "bg-emerald-100 text-emerald-800",
};

export default function Investigations() {
  const nav = useNavigate();
  const { investigationRole } = useAuth();
  const isLead = investigationRole === "lead_investigator";
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", standard_id: "", requirement_ids: "", incident_summary: "" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/investigations").then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title.trim()) return toast.error("Enter a case title");
    setSaving(true);
    try {
      const { data } = await api.post("/investigations", {
        title: form.title,
        standard_id: form.standard_id || null,
        requirement_ids: form.requirement_ids ? form.requirement_ids.split(",").map((s) => s.trim()).filter(Boolean) : [],
        incident_summary: form.incident_summary,
      });
      toast.success("Investigation opened");
      setOpen(false);
      setForm({ title: "", standard_id: "", requirement_ids: "", incident_summary: "" });
      nav(`/investigations/${data.id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="p-8 space-y-6" data-testid="investigations-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-display font-black text-3xl tracking-tight flex items-center gap-2">
            <MagnifyingGlass size={28} weight="fill" /> Investigations
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Root-cause investigations for potential noncompliance. Find the root and contributing
            causes, mitigate recurrence, and prepare self-report / self-log packages. You see only
            cases you are assigned to.
          </p>
          <div className="text-xs mt-2 inline-flex items-center gap-1.5 text-muted-foreground">
            <Scales size={14} /> Your role: <span className="font-semibold">{ROLE_LABEL[investigationRole] || "—"}</span>
          </div>
        </div>
        {isLead && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-investigation-btn" className="rounded-none"><Plus size={16} className="mr-1" />New Investigation</Button>
            </DialogTrigger>
            <DialogContent className="rounded-none">
              <DialogHeader><DialogTitle>Open Investigation</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Case title *</Label><Input data-testid="inv-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-none mt-1" placeholder="e.g. PRC-005 missed maintenance interval" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Standard</Label><Input data-testid="inv-standard-input" value={form.standard_id} onChange={(e) => setForm({ ...form, standard_id: e.target.value })} className="rounded-none mt-1" placeholder="PRC-005-6" /></div>
                  <div><Label>Requirement(s)</Label><Input data-testid="inv-reqs-input" value={form.requirement_ids} onChange={(e) => setForm({ ...form, requirement_ids: e.target.value })} className="rounded-none mt-1" placeholder="R1, R3" /></div>
                </div>
                <div><Label>Incident summary</Label><Textarea data-testid="inv-summary-input" value={form.incident_summary} onChange={(e) => setForm({ ...form, incident_summary: e.target.value })} className="rounded-none mt-1 min-h-[90px]" placeholder="What happened, when discovered, and how." /></div>
                <Button data-testid="inv-create-submit" onClick={create} disabled={saving} className="rounded-none w-full">{saving ? "Opening…" : "Open Investigation"}</Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {items.length === 0 ? (
        <div data-testid="inv-empty" className="border border-dashed border-border p-12 text-center text-muted-foreground">
          No investigations assigned to you yet.{isLead ? " Open one to get started." : ""}
        </div>
      ) : (
        <div className="grid gap-3" data-testid="inv-list">
          {items.map((it) => (
            <button key={it.id} data-testid={`inv-card-${it.id}`} onClick={() => nav(`/investigations/${it.id}`)}
              className="text-left border border-border bg-card hover:border-primary transition-colors p-4 rounded-none">
              <div className="flex items-center justify-between gap-3">
                <div className="font-semibold">{it.title}</div>
                <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_STYLE[it.status] || ""}`}>{it.status?.replace("_", " ")}</span>
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {it.standard_id ? `${it.standard_id}${it.requirement_ids?.length ? " " + it.requirement_ids.join(", ") : ""} · ` : ""}
                Lead: {it.lead_name} · Team: {(it.team_member_ids || []).length}
                {it.violation_indicated ? " · ⚠ Violation indicated" : ""}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
